import { inject, Injectable, signal } from '@angular/core';
import { AlertService, AlertType } from '../alert.service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import {
  AnswerCreateRequest,
  AnswerResponse,
  AnswerUpdateRequest,
  IEmployeeSimple,
  IPagination,
  IReviewCycleEmployeeResponse,
  MarkTemplateCompletedRequest,
  MarkTemplateCompletedResponse,
  PeerFeedbackDetails,
  PeerNominationUpdateRequest,
  QuestionAnswerResponse,
  ReviewCyclePeersResponse,
  RevieweePeeringInfo,
} from '../../models';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PeersService {
  private readonly alertService = inject(AlertService);
  private readonly httpClient = inject(HttpClient);
  private readonly translate = inject(TranslateService);

  private readonly urlBase: string = 'api/performance-management/review-cycle';
  private readonly urlUserBase: string = 'api/performance-management/review-cycle-user';
  private readonly urlConfig: string = 'api/performance-management/review-cycle-config';

  constructor() {}

  filteredRevieweePeerInfos = signal<IPagination<RevieweePeeringInfo>>({
    items: [],
    totalElements: 0,
    config: { sortColumns: [] },
  });

  // Peer selection phase APIs
  searchRevieweesPeeringInfo(
    reviewCycleId: number,
    params: HttpParams,
    searchTerm: string = '',
    leadId?: number,
    leadOrAdmin?: string,
  ): Observable<IPagination<RevieweePeeringInfo>> {
    let url = `${this.urlBase}/${reviewCycleId}/peers/${leadOrAdmin}`;
    let queryParams = searchTerm ? params.append('name', searchTerm) : params;

    if (leadId) {
      queryParams = queryParams.append('leadId', leadId.toString());
    }
    return this.httpClient
      .get<IPagination<RevieweePeeringInfo>>(url, {
        params: queryParams,
      })
      .pipe(
        catchError(this.handleError),
        switchMap((response: IPagination<RevieweePeeringInfo>) => {
          const content = response?.content ?? [];
          if (content.length === 0) {
            return of({ ...response, content: [] });
          }

          const employeeRequests = content.map((reviewee) =>
            this.checkEmployeeLeadStatus(reviewee.reviewer.employeeId).pipe(
              map((isLead) => ({
                ...reviewee,
                isLead,
              })),
            ),
          );

          return forkJoin(employeeRequests).pipe(
            map((updatedItems) => ({
              ...response,
              content: updatedItems,
            })),
          );
        }),
        tap((response: IPagination<RevieweePeeringInfo>) => {
          const items = (response?.content ?? []).map((el: RevieweePeeringInfo) => ({
            ...el,
            reviewer: {
              ...el.reviewer,
              photo: el.reviewer?.photo ? `data:image/png;base64,${el.reviewer.photo}` : null,
            },
          }));

          this.filteredRevieweePeerInfos.set({
            items,
            totalElements: response.totalElements,
            config: { sortColumns: [] },
          });
        }),
      );
  }

  getLeadPeersByEmployeeId(
    reviewCycleId: number,
    employeeId: number,
    params: any,
  ): Observable<ReviewCyclePeersResponse> {
    return this.httpClient
      .get<any>(`${this.urlBase}/${reviewCycleId}/peers/lead/${employeeId}`, { params })
      .pipe(catchError(this.handleError));
  }

  getAssignedParticipants(reviewCycleId: number): Observable<IEmployeeSimple[]> {
    return this.httpClient
      .get<IEmployeeSimple[]>(`${this.urlConfig}/${reviewCycleId}/participant-selection/assigned`)
      .pipe(catchError(this.handleError));
  }

  getAssignedParticipantsFull(reviewCycleId: number): Observable<IReviewCycleEmployeeResponse[]> {
    return this.httpClient
      .get<
        IReviewCycleEmployeeResponse[]
      >(`${this.urlConfig}/${reviewCycleId}/participant-selection/assigned-full`)
      .pipe(catchError(this.handleError));
  }

  getAssignedParticipantsFullForUser(
    reviewCycleId: number,
  ): Observable<IReviewCycleEmployeeResponse[]> {
    return this.httpClient
      .get<IReviewCycleEmployeeResponse[]>(`${this.urlUserBase}/${reviewCycleId}/participants`)
      .pipe(catchError(this.handleError));
  }

  getSharedPacketsForUser(reviewCycleId: number): Observable<IReviewCycleEmployeeResponse[]> {
    return this.httpClient
      .get<IReviewCycleEmployeeResponse[]>(`${this.urlUserBase}/${reviewCycleId}/shared-packets`)
      .pipe(catchError(this.handleError));
  }

  updateLeadPeersSelection(reviewCycleId: number, requestBody: any): Observable<any> {
    return this.httpClient
      .put<any>(`${this.urlBase}/${reviewCycleId}/peers/lead`, requestBody)
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEERS_SELECTION'),
          ),
        ),
      );
  }

  deletePeerAssignment(reviewCycleId: number, assignmentId: number): Observable<any> {
    return this.httpClient
      .delete<any>(`${this.urlBase}/${reviewCycleId}/peers/lead/${assignmentId}`)
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant(
              'ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEER_ASSIGNMENT_DELETE',
            ),
          ),
        ),
      );
  }

  updatePeerAssignmentStatus(reviewCycleId: number, requestBody: any): Observable<any> {
    return this.httpClient
      .put<any>(`${this.urlBase}/${reviewCycleId}/peers/lead/nomination-status`, requestBody)
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant(
              'ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEER_ASSIGNMENT_STATUS_UPDATE',
            ),
          ),
        ),
      );
  }

  approveAllPendingPeers(
    cycleId: number,
    employeeId: number,
    request: PeerNominationUpdateRequest[],
  ): Observable<any> {
    return this.httpClient
      .put<any>(
        `${this.urlBase}/${cycleId}/peers/lead/nomination-status/${employeeId}/approve-multiple`,
        request,
      )
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant(
              'ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_PEER_ASSIGNMENT_STATUS_UPDATE',
            ),
          ),
        ),
      );
  }

  // Feedback phase APIs
  searchRevieweesGivenFeedback(
    reviewCycleId: number,
    params: HttpParams,
    searchTerm: string = '',
    leadId: number,
    leadOrAdmin: string,
  ): Observable<any> {
    let url = `${this.urlBase}/${reviewCycleId}/peers/${leadOrAdmin}/feedback`;
    let queryParams = searchTerm ? params.append('name', searchTerm) : params;
    if (leadId) {
      queryParams = queryParams.append('leadId', leadId.toString());
    }
    return this.httpClient
      .get<IPagination<any>>(url, {
        params: queryParams,
      })
      .pipe(
        catchError(this.handleError),
        switchMap((response: IPagination<RevieweePeeringInfo>) => {
          const content = response?.content ?? [];
          if (content.length === 0) {
            return of({ ...response, content: [] });
          }

          const employeeRequests = content.map((reviewee) =>
            this.checkEmployeeLeadStatus(reviewee.reviewer.employeeId).pipe(
              map((isLead) => ({
                ...reviewee,
                isLead,
              })),
            ),
          );

          return forkJoin(employeeRequests).pipe(
            map((updatedItems) => ({
              ...response,
              content: updatedItems,
            })),
          );
        }),
        tap((response: IPagination<RevieweePeeringInfo>) => {
          const items = (response?.content ?? []).map((el: RevieweePeeringInfo) => ({
            ...el,
            reviewer: {
              ...el.reviewer,
              photo: el.reviewer?.photo ? `data:image/png;base64,${el.reviewer.photo}` : null,
            },
          }));

          this.filteredRevieweePeerInfos.set({
            items,
            totalElements: response.totalElements,
            config: { sortColumns: [] },
          });
        }),
      );
  }

  getFeedbackByEmployeeId(
    reviewCycleId: number,
    employeeId: number,
  ): Observable<PeerFeedbackDetails> {
    return this.httpClient
      .get<PeerFeedbackDetails>(
        `${this.urlBase}/${reviewCycleId}/peers/lead/feedback/${employeeId}`,
      )
      .pipe(
        catchError((error) => {
          return this.handleError(error);
        }),
      );
  }

  getFeedbackAnswersByAssignment(assignmentId: number): Observable<QuestionAnswerResponse> {
    return this.httpClient
      .get<QuestionAnswerResponse>(`${this.urlBase}/answers/feedback/${assignmentId}`)
      .pipe(catchError((error) => this.handleError(error)));
  }

  private handleSuccess(message: string) {
    this.alertService.showAlert(message, AlertType.success);
  }

  private readonly handleError = (error: HttpErrorResponse): Observable<never> => {
    this.alertService.showAlert(error.error, AlertType.error);
    return throwError(() => error);
  };

  getAnswersByAssignment(assignmentId: number): Observable<AnswerResponse> {
    return this.httpClient
      .get<AnswerResponse>(`${this.urlBase}/answers/assignment/${assignmentId}`)
      .pipe(catchError((error) => this.handleError(error)));
  }

  createAnswers(request: AnswerCreateRequest): Observable<AnswerResponse> {
    return this.httpClient
      .post<AnswerResponse>(`${this.urlBase}/answers`, request)
      .pipe(catchError((error) => this.handleError(error)));
  }

  updateAnswers(request: AnswerUpdateRequest): Observable<AnswerResponse> {
    return this.httpClient
      .put<AnswerResponse>(`${this.urlBase}/answers`, request)
      .pipe(catchError((error) => this.handleError(error)));
  }

  markTemplateCompleted(
    request: MarkTemplateCompletedRequest,
  ): Observable<MarkTemplateCompletedResponse> {
    return this.httpClient
      .post<MarkTemplateCompletedResponse>(`${this.urlBase}/answers/complete`, request)
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant('ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_COMPLETED'),
          ),
        ),
      );
  }

  markTemplateUncompleted(
    request: MarkTemplateCompletedRequest,
  ): Observable<MarkTemplateCompletedResponse> {
    return this.httpClient
      .post<MarkTemplateCompletedResponse>(`${this.urlBase}/answers/complete`, request)
      .pipe(
        catchError(this.handleError),
        tap(() =>
          this.handleSuccess(
            this.translate.instant(
              'ADMIN.REVIEW_CYCLES.MESSAGES.SUCCESSFUL_REVIEW_REVERTED_TO_DRAFT',
            ),
          ),
        ),
      );
  }

  checkEmployeeLeadStatus(employeeId: number): Observable<boolean> {
    return this.httpClient.get<any>(`/api/employees/${employeeId}`).pipe(
      map((response) => response?.lead || false),
      catchError((error) => {
        console.error('Error checking lead status:', error);
        return of(false);
      }),
    );
  }
}
